import type {
  AIWorkoutResponse,
  AIStep,
  WorkoutSegment,
  SportType,
  ZoneDefinition,
  RunIntensity,
} from '@/types';

// Default assumptions for math
const DEFAULT_THRESHOLD_SPEED_MS = 3.33; // 5:00 min/km or 12 km/h. Used to convert Dist -> Time.

// Helper: Parse intensity string to a numeric factor (0.0 - 2.0)
const parseIntensity = (intensityStr: string, zones: ZoneDefinition[], sport: SportType): { val: number, text: string } => {
    // Match ranges BEFORE the destructive strip below. We accept "95-100%",
    // "95–105% FTP", "85 to 95%", "Z3-Z4". For numeric % ranges return the
    // midpoint; for zone ranges (e.g. "Z3-Z4") fall through to the zone code
    // below, which already understands a single Z3 / Z4.
    const rawLower = intensityStr.toLowerCase();
    const pctRange = rawLower.match(/(\d+(?:\.\d+)?)\s*(?:-|–|—|to|do)\s*(\d+(?:\.\d+)?)\s*%/);
    if (pctRange) {
        const lo = parseFloat(pctRange[1]) / 100;
        const hi = parseFloat(pctRange[2]) / 100;
        if (Number.isFinite(lo) && Number.isFinite(hi) && lo > 0 && hi > 0 && hi < 5) {
            return { val: (lo + hi) / 2, text: intensityStr };
        }
    }

    const str = rawLower.replace(/[^a-z0-9%.]/g, ''); // clean string, keep % and .

    // 0. PRIORITY: Explicit "Luz" / "Rest" / "Recovery" keywords
    // Ensures that even if AI gets confused, these keywords force Z1.
    // Added 'swobod' for "swobodnie"
    if (str.includes('luz') || str.includes('odpocz') || str.includes('rest') || str.includes('recov') || str.includes('easy') || str.includes('spokoj') || str.includes('trucht') || str.includes('swobod')) {
        const z1 = zones[0]; // Z1 / S1
        
        // FIX: For bike, mathematical average of Z1 (0-55%) is ~28%, which is too low for smart trainers. 
        // Standard active recovery is usually 50%.
        if (sport === 'bike') {
            return { val: 0.50, text: "Recovery (S1)" };
        }

        return { val: (z1.min + z1.max) / 2, text: "Recovery (S1)" };
    }

    // 1. Check for single percentage (e.g. "105%"). Guard against accidental
    // multi-number strings like "95100%" (a dashed range that lost its dash):
    // the cleaned string must look like a single number followed by a percent.
    if (/^\d+(?:\.\d+)?%$/.test(str)) {
        const num = parseFloat(str) / 100;
        if (Number.isFinite(num) && num > 0 && num < 5) {
            return { val: num, text: intensityStr };
        }
    }

    // 1b. Leading percent glued to zones/words: "97%z4threshold", "102%thresholdoverunder"
    const leadingPctGlue = str.match(/^(\d+(?:\.\d+)?)%/);
    if (leadingPctGlue) {
        const num = parseFloat(leadingPctGlue[1]) / 100;
        if (Number.isFinite(num) && num > 0 && num < 5) {
            return { val: num, text: intensityStr };
        }
    }

    // 2. Specific Overrides for Running semantics
    // "Marsz" / "Walk" -> Needs to be lower than typical Z1 jog.
    if (str.includes('marsz') || str.includes('walk')) {
        return { val: 0.35, text: "Walk" }; // 35% of Threshold is a slow walk
    }
    
    // "Rytmy" / "Strides" -> High intensity
    if (str.includes('rytm') || str.includes('stride') || str.includes('sprint')) {
        // Use max zone avg if available, or 1.30 (Anaerobic)
        const maxZone = zones[zones.length - 1];
        const val = maxZone ? (maxZone.min + maxZone.max) / 2 : 1.30;
        return { val: val, text: "Strides" };
    }

    // 3. Check for Exact Zone IDs (Z1, S3, Z5a, etc.)
    const zoneById = zones.find(z => str === z.id.toLowerCase());
    if (zoneById) {
        // CYCLING CONSISTENCY FIX:
        // User requested "Normal S2" to be the "Upper Half" (65-75%).
        // Standard Math avg of 0.56-0.75 is ~0.66. We manually override to 0.70 for consistency.
        if (sport === 'bike' && (zoneById.id === 'Z2' || zoneById.id === 'S2')) {
            return { val: 0.70, text: zoneById.name };
        }

        // Same "Z1 midpoint is too low for smart trainers" rationale as the
        // keyword branch above: clamp bike Z1/S1 to at least ~50% FTP. Without
        // this, a user with the Coggan default (0-55%) gets a 27.5% target
        // that no trainer will execute meaningfully.
        if (sport === 'bike' && (zoneById.id === 'Z1' || zoneById.id === 'S1')) {
            const avg = (zoneById.min + zoneById.max) / 2;
            return { val: Math.max(0.50, avg), text: zoneById.name };
        }

        const avg = (zoneById.min + zoneById.max) / 2;
        return { val: avg, text: zoneById.name };
    }

    // 4. Robust Regex for "Z2", "S5a", "Strefa 5", "Zone 5"
    // Matches: s1, z1, s5a, z5b, strefa5, zone5
    // Adjusted Regex: (strefa|zone|[sz]) -> Group 1, (\d) -> Group 2 (Number)
    const zoneMatch = str.match(/(strefa|zone|[sz])(\d)([abc])?/);
    
    if (zoneMatch) {
        const zoneNum = parseInt(zoneMatch[2]);
        const subZone = zoneMatch[3]; // 'a', 'b', or 'c'

        // Construct target ID (e.g., Z5a) to search
        let targetId = `Z${zoneNum}${subZone || ''}`;
        
        // Try exact match first
        let matchedZone = zones.find(z => z.id.toUpperCase() === targetId);
        
        // If not found and no subzone specified (e.g. user said "Z5" but zones are Z5a, Z5b), 
        // fallback to 'a' or just the first matching number
        if (!matchedZone && !subZone) {
             matchedZone = zones.find(z => z.id.startsWith(`Z${zoneNum}`));
        }

        if (matchedZone) {
             // Apply same S2 override logic here if regex matched Z2
             if (sport === 'bike' && zoneNum === 2) {
                 return { val: 0.70, text: matchedZone.name };
             }

             const avg = (matchedZone.min + matchedZone.max) / 2;
             return { val: avg, text: matchedZone.name };
        }
    }

    // 5. Keyword matching (Fallback)
    if (str.includes('warm') || str.includes('rozgrz')) {
         const z1 = zones[0];
         return { val: (z1.min + z1.max) / 2, text: "Warmup" };
    }
    
    // Endurance / Z2 keywords
    if (str.includes('endura') || str.includes('tlen') || str.includes('z2') || str.includes('s2')) {
        if (sport === 'bike') return { val: 0.70, text: "Endurance (S2)" };
        const z2 = zones[1]; // Z2
        return { val: (z2.min + z2.max)/2, text: "Endurance" };
    }

    if (str.includes('tempo') || str.includes('s3') || str.includes('z3')) {
        // Try to find a zone named "Tempo" first
        const tempoZone = zones.find(z => z.name.toLowerCase().includes('tempo')) || zones[2];
        return { val: (tempoZone.min + tempoZone.max) / 2, text: "Tempo" };
    }
    if (str.includes('thresh') || str.includes('prog') || str.includes('próg') || str.includes('z4') || str.includes('s4')) {
         // Try to find a zone named "Threshold" first
        const threshZone = zones.find(z => z.name.toLowerCase().includes('threshold')) || zones[3];
        // If it's pure threshold keyword, typically standardizing to 1.00 is safest for workouts
        return { val: 1.00, text: "Threshold" }; 
    }
    
    // Z5 keywords
    if (str.includes('vo2') || str.includes('max') || str.includes('hard') || str.includes('moc') || str.includes('z5') || str.includes('s5') || str.includes('ac')) {
         // Find a zone named VO2 Max or fallback to typical index
        const vo2Zone = zones.find(z => z.name.toLowerCase().includes('vo2')) || zones[4];
        if (vo2Zone) return { val: (vo2Zone.min + vo2Zone.max) / 2, text: "VO2 Max" };
    }

    // Fallback: Lower default to avoid "too fast" assumptions for unknown text
    return { val: 0.50, text: "Aerobic" };
};

// Helper: Convert AI Step duration to Seconds
const calculateDuration = (val: number, unit: string, intensityFactor: number): number => {
    if (unit === 'min') return Math.round(val * 60);
    if (unit === 'sec') return Math.round(val);
    
    // Distance based: Time = Distance / Speed
    // Speed = BaseSpeed * IntensityFactor
    let meters = unit === 'km' ? val * 1000 : val;
    
    // Safety: don't divide by zero or very low intensity
    // For walking (0.35), speed drops significantly
    const effectiveIntensity = Math.max(0.2, intensityFactor);
    const estimatedSpeed = DEFAULT_THRESHOLD_SPEED_MS * effectiveIntensity;
    
    return Math.round(meters / estimatedSpeed);
};

// Helper: Clean text from leading dashes/arrows
const cleanText = (text: string | undefined): string | undefined => {
    if (!text) return undefined;
    return text.replace(/^[->\s]+/, '').trim();
};

export const processAIResponseToSegments = (
    aiResponse: AIWorkoutResponse, 
    zones: ZoneDefinition[],
    sport: SportType
): WorkoutSegment[] => {
    console.log('🔄 [workoutProcessor] processAIResponseToSegments() START');
    console.log('   - AI Response:', aiResponse);
    console.log('   - Steps count:', aiResponse.steps?.length || 0);
    console.log('   - Sport:', sport);
    console.log('   - Zones count:', zones.length);
    console.log('   - Zones:', zones);
    
    // -----------------------------------------------------
    // PASS 1: Pre-calculate durations and balance Warmup/Cooldown
    // -----------------------------------------------------
    console.log('⏳ [workoutProcessor] PASS 1: Pre-calculating durations...');
    const parsedSteps = aiResponse.steps.map((step, idx) => {
        console.log(`   [Step ${idx}]`, step);
        const safeWork = step.work || { duration: 1, unit: 'min', intensity: 'recovery' };
        const workIntensityText = safeWork.intensity || 'recovery';
        const workInt = parseIntensity(workIntensityText, zones, sport);
        const workDur = calculateDuration(safeWork.duration || 1, safeWork.unit || 'min', workInt.val);
        
        let recInt = { val: 0.5, text: 'Recovery' };
        let recDur = 0;
        
        if (step.type === 'interval' && step.recovery) {
             const safeRecovery = step.recovery;
             const recIntensityText = safeRecovery.intensity || 'recovery';
             recInt = parseIntensity(recIntensityText, zones, sport);
             recDur = calculateDuration(safeRecovery.duration || 1, safeRecovery.unit || 'min', recInt.val);
        }
        
        const repeats = step.repeats || 1;
        const totalDur = (workDur + recDur) * repeats;
        
        return {
            step,
            workInt,
            workDur,
            recInt,
            recDur,
            totalDur,
            // Identify variable buffers: Explicitly type 'warmup' or 'cooldown'
            isVariable: step.type === 'warmup' || step.type === 'cooldown'
        };
    });

    // If Total Duration is known, recalculate Warmup and Cooldown
    if (aiResponse.totalDuration && aiResponse.totalDuration > 0) {
        const targetSec = aiResponse.totalDuration * 60;
        
        // Sum "Locked" Time (Intervals, Specific Steady States)
        const lockedSec = parsedSteps.reduce((sum, p) => {
            return p.isVariable ? sum : sum + p.totalDur;
        }, 0);
        
        const bufferSec = targetSec - lockedSec;
        
        // Only apply balancing if there is remaining time to distribute
        if (bufferSec > 0) {
            const warmupIdx = parsedSteps.findIndex(p => p.step.type === 'warmup');
            const cooldownIdx = parsedSteps.findIndex(p => p.step.type === 'cooldown');
            
            if (warmupIdx !== -1 && cooldownIdx !== -1) {
                // Split buffer equally between Warmup and Cooldown
                const split = Math.floor(bufferSec / 2);
                
                parsedSteps[warmupIdx].totalDur = split;
                parsedSteps[warmupIdx].workDur = split; 
                
                parsedSteps[cooldownIdx].totalDur = bufferSec - split; // Handle odd seconds
                parsedSteps[cooldownIdx].workDur = bufferSec - split;
            } 
            else if (warmupIdx !== -1) {
                // Only Warmup exists
                parsedSteps[warmupIdx].totalDur = bufferSec;
                parsedSteps[warmupIdx].workDur = bufferSec;
            } 
            else if (cooldownIdx !== -1) {
                // Only Cooldown exists
                parsedSteps[cooldownIdx].totalDur = bufferSec;
                parsedSteps[cooldownIdx].workDur = bufferSec;
            }
        }
    }

    // -----------------------------------------------------
    // PASS 2: Generate Segments (Applying Structure Logic)
    // -----------------------------------------------------
    const segments: WorkoutSegment[] = [];
    let currentTime = 0;

    parsedSteps.forEach(p => {
        const { step, workInt, workDur, recInt, recDur } = p; // Use the (potentially updated) workDur
        const isWarmup = step.type === 'warmup';
        const isCooldown = step.type === 'cooldown';
        
        // BIKE STRUCTURE LOGIC
        // Lower threshold to 360s (6 mins) to allow the 5min Fixed + 1min Steady structure
        if (sport === 'bike' && isWarmup && workDur >= 360) {
             const s1Dur = 300; // 5 min fixed
             const s2Dur = workDur - s1Dur;
             
             const lowS2Min = 0.56;
             const lowS2Max = 0.65;
             
             let highS2Val = workInt.val;
             if (highS2Val < 0.60) highS2Val = 0.70;

             // Segment 1: 5 min Low S2 (Steady or Slight Ramp)
             segments.push({
                 type: 'Warmup',
                 time: currentTime,
                 duration: s1Dur,
                 powerLow: lowS2Min, 
                 powerHigh: lowS2Max, 
                 text: "Warm Up (Low S2)"
             });
             currentTime += s1Dur;

             // Segment 2: Remaining time -> High S2 (Steady)
             segments.push({
                 type: 'SteadyState',
                 time: currentTime,
                 duration: s2Dur,
                 powerLow: highS2Val, 
                 powerHigh: highS2Val,
                 text: "Warm Up (High S2)"
             });
             currentTime += s2Dur;
             return; 
        }

        if (sport === 'bike' && isCooldown && workDur >= 360) {
            const s1Dur = 300; // 5 min fixed at end
            const steadyDur = workDur - s1Dur;

            const lowS2Min = 0.56;
            const lowS2Max = 0.65;
            
            let steadyVal = workInt.val;
            // Ensure steady state before cool down isn't weirdly low
            if (steadyVal < lowS2Max) steadyVal = lowS2Max;
            else if (steadyVal > 0.75 && step.work.intensity.includes('S2')) steadyVal = 0.70;

            // Segment 1: Steady at parsed (or clamped) intensity. 
            segments.push({
                type: 'SteadyState',
                time: currentTime,
                duration: steadyDur,
                powerLow: steadyVal,
                powerHigh: steadyVal,
                text: "Cool Down (Steady)"
            });
            currentTime += steadyDur;

            // Segment 2: Last 5 mins -> Low S2
            segments.push({
                type: 'Cooldown',
                time: currentTime,
                duration: s1Dur,
                powerLow: lowS2Max, // Start of ramp
                powerHigh: lowS2Min, // End of ramp
                text: "Cool Down (Low S2)"
            });
            currentTime += s1Dur;
            return;
        }

        // STANDARD PROCESSING
        if (step.type === 'interval') {
            const repeats = step.repeats || 1;
            for (let i = 0; i < repeats; i++) {
                // Work
                segments.push({
                    type: 'IntervalsT',
                    time: currentTime,
                    duration: workDur,
                    powerLow: workInt.val,
                    powerHigh: workInt.val,
                    text: cleanText(step.text) || `Interval ${i+1}/${repeats}`
                });
                currentTime += workDur;

                // Recovery
                segments.push({
                    type: 'IntervalsT',
                    time: currentTime,
                    duration: recDur,
                    powerLow: recInt.val,
                    powerHigh: recInt.val,
                    text: "Recovery"
                });
                currentTime += recDur;
            }
        } else {
            // Steady, Warmup, Cooldown
            segments.push({
                type: isWarmup ? 'Warmup' : isCooldown ? 'Cooldown' : 'SteadyState',
                time: currentTime,
                duration: workDur,
                powerLow: workInt.val,
                powerHigh: workInt.val,
                text: cleanText(step.text) || workInt.text
            });
            currentTime += workDur;
        }
    });

    console.log('✅ [workoutProcessor] Processing complete');
    console.log('   - Final segments count:', segments.length);
    console.log('   - Final segments:', segments);
    console.log('🎉 [workoutProcessor] processAIResponseToSegments() SUCCESS');
    
    return segments;
};